import React, { useEffect, useState } from "react";
import { Mail, Phone, MessageCircle, Trash2, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg, waLink } from "@/lib/api";
import { AdminCard, PageTitle } from "@/pages/admin/AdminLayout";
import { Switch } from "@/components/ui/switch";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");

export default function AdminInquiries() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");

  const load = () => adminApi.get("/admin/inquiries").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggle = async (i) => {
    try {
      await adminApi.patch(`/admin/inquiries/${i.id}`, { handled: !i.handled });
      setList((l) => l.map((x) => (x.id === i.id ? { ...x, handled: !x.handled } : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (i) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await adminApi.delete(`/admin/inquiries/${i.id}`);
      setList((l) => l.filter((x) => x.id !== i.id));
      toast.success("Richiesta eliminata");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = list.filter((x) => (filter === "all" ? true : filter === "open" ? !x.handled : x.handled));

  return (
    <div data-testid="admin-inquiries">
      <PageTitle title="Richieste" sub="Messaggi dal modulo contatti e richieste date dalla home."
        right={
          <div className="flex gap-2">
            {[["open", "Aperte"], ["done", "Gestite"], ["all", "Tutte"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`admin-inquiries-filter-${k}`} className={`px-4 py-2 rounded-full text-sm border ${filter === k ? "bg-ocean text-ivory border-ocean" : "bg-white border-sand text-ocean"}`}>{l}</button>
            ))}
          </div>
        } />
      {loading ? <p className="text-ocean/50">Caricamento...</p> : shown.length === 0 ? (
        <AdminCard className="p-12 text-center text-ocean/50" ><span data-testid="admin-inquiries-empty">Nessuna richiesta.</span></AdminCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {shown.map((i) => (
            <AdminCard key={i.id} className={`p-5 ${i.handled ? "opacity-70" : ""}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full ${i.kind === "dates" ? "bg-sea/15 text-sea" : "bg-blue-50 text-blue-800"}`}>{i.kind === "dates" ? "Richiesta date" : "Contatto"}</span>
                  <p className="mt-3 font-medium text-ocean">{i.name}</p>
                  <p className="text-xs text-ocean/50">{fmt(i.created_at)} · {(i.lang || "en").toUpperCase()}</p>
                </div>
                <label className="flex items-center gap-2 text-xs text-ocean/60">
                  Gestita <Switch checked={i.handled} onCheckedChange={() => toggle(i)} data-testid={`admin-inquiry-handled-${i.id}`} />
                </label>
              </div>
              {(i.date_from || i.date_to || i.guests) && (
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-ocean/70">
                  {(i.date_from || i.date_to) && <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} />{i.date_from || "?"} → {i.date_to || "?"}</span>}
                  {i.guests && <span className="inline-flex items-center gap-1.5"><Users size={13} />{i.guests}</span>}
                </div>
              )}
              {(i.message || i.interests) && <p className="mt-3 text-sm text-ocean/75 bg-ivory rounded-xl p-3 whitespace-pre-line">{i.message || i.interests}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                <a href={`mailto:${i.email}`} className="inline-flex items-center gap-1.5 text-ocean/70 hover:text-sea"><Mail size={14} />{i.email}</a>
                {i.phone && <a href={`tel:${i.phone}`} className="inline-flex items-center gap-1.5 text-ocean/70"><Phone size={14} />{i.phone}</a>}
                {i.phone && <a href={waLink(i.phone, `Ciao ${i.name}, ti scriviamo da AMS Travel.`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-emerald-700"><MessageCircle size={14} />WhatsApp</a>}
                <button onClick={() => remove(i)} className="ml-auto text-rose-700 inline-flex items-center gap-1" data-testid={`admin-inquiry-delete-${i.id}`}><Trash2 size={14} /></button>
              </div>
            </AdminCard>
          ))}
        </div>
      )}
    </div>
  );
}
